/**
 * /messages/$conversationId — single-conversation pane.
 *
 * Phase 4b: branch on `conversation.type` so DMs render
 * `<ConversationHeader>` and channels render `<ChannelHeader>`. The rest
 * of the pane (timeline + composer + per-conversation realtime) is
 * type-agnostic.
 *
 * Phase 4e: an `<Outlet/>` renders the optional thread sidebar
 * (`t/$parentId.tsx`) to the right of the timeline. The Outlet renders
 * NOTHING when no child route matches (the index route is null), so the
 * timeline takes the full pane until the user opens a thread.
 *
 * Phase 4f: this route owns the in-conversation search string. The
 * header (DM or channel) renders the input; the timeline area branches
 * on `searchActive` (trimmed length ≥ 2) and renders either the regular
 * `<MessageList>` or `<MessageSearchResults>`. The composer + thread
 * overlay + typing indicator stay mounted regardless. State resets to
 * `''` on `conversationId` change so navigating between conversations
 * doesn't carry a stale query.
 */

import { Link, Outlet, createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/client'
import { ChannelHeader } from '@/features/chat/components/ChannelHeader'
import { ChatComposer, type ChatComposerHandle } from '@/features/chat/components/ChatComposer'
import { ChatDropZone } from '@/features/chat/components/ChatDropZone'
import { ConversationHeader } from '@/features/chat/components/ConversationHeader'
import { MessageList } from '@/features/chat/components/MessageList'
import { MessageSearchResults } from '@/features/chat/components/MessageSearchResults'
import { TypingIndicator } from '@/features/chat/components/TypingIndicator'
import { useConversation } from '@/features/chat/hooks/use-conversation'
import { useSearchConversationMessages } from '@/features/chat/hooks/use-search-conversation-messages'
import { useChatRealtime } from '@/features/chat/realtime/use-chat-realtime'
import { useViewingSignal } from '@/features/chat/realtime/use-viewing-signal'
import { useMe } from '@/features/me/use-me'

interface ConversationSearch {
  /**
   * Notification deep-link target — message id to scroll into view +
   * flash on mount. Read by `<MessageList>`, cleared after the focus
   * pass fires so re-renders don't re-fire.
   */
  focusMessage?: string
}

export const Route = createFileRoute('/messages/$conversationId')({
  component: ConversationRouteComponent,
  validateSearch: (search: Record<string, unknown>): ConversationSearch => ({
    focusMessage: typeof search.focusMessage === 'string' ? search.focusMessage : undefined,
  }),
})

function ConversationRouteComponent() {
  const { t } = useTranslation('chat')
  const { conversationId } = Route.useParams()
  const { focusMessage } = Route.useSearch()
  const navigate = Route.useNavigate()
  const conversation = useConversation(conversationId)
  const me = useMe()
  const currentUserId = me.data?.user.id ?? null

  // Mount the per-conversation realtime listener. Self-broadcasts skip
  // (the optimistic mutation already added the row); other-author frames
  // invalidate the timeline cache to refetch.
  useChatRealtime({ conversationId, currentUserId })

  // Conversation pane root — interaction here (pointer / keyboard /
  // scroll) keeps the AFK timer alive for the "actively viewing"
  // suppression signal.
  const paneRef = useRef<HTMLDivElement | null>(null)

  // Tell the BE we're actively viewing this conversation so it suppresses
  // reaction push notifications while we're reading. Event-driven (open /
  // visibility / AFK / unmount) — no periodic send. Socket lifecycle is
  // owned by <WebSocketProvider>; this only gates the signal.
  useViewingSignal({ conversationId, surfaceRef: paneRef })

  // Clear the focus search param after the MessageList consumes it so
  // re-renders (or back-nav to the same conversation) don't keep
  // re-firing the scroll-flash. `replace: true` so we don't push a
  // history entry on the cleanup.
  const handleFocusConsumed = useCallback(() => {
    navigate({ to: '.', search: {}, replace: true })
  }, [navigate])

  // Phase 4f — in-conversation search. Reset when the active conversation
  // changes so cross-conversation navigation always lands on the
  // timeline, not a stale results pane.
  const [searchQuery, setSearchQuery] = useState('')
  // biome-ignore lint/correctness/useExhaustiveDependencies: deliberate one-shot — re-fire only when conversationId changes; setState identity is stable.
  useEffect(() => {
    setSearchQuery('')
  }, [conversationId])

  const trimmedQuery = searchQuery.trim()
  const searchActive = trimmedQuery.length >= 2
  const search = useSearchConversationMessages(conversationId, searchQuery)

  const summary = conversation.data ?? null
  const channelMembers = summary?.type === 'channel' ? (summary.members ?? undefined) : undefined

  // Composer placeholder derived from the conversation context:
  //   DM     → "Message <first-name>"
  //   Channel → "Message #<name>"
  // Falls back to "Write a message…" while the summary is still loading.
  let composerPlaceholder = t('composer.defaultPlaceholder')
  if (summary?.type === 'dm') {
    const peerName = summary.peer?.name ?? ''
    const firstName = peerName.trim().split(/\s+/)[0]
    composerPlaceholder = firstName
      ? t('composer.dmPlaceholder', { name: firstName })
      : t('composer.dmPlaceholderGeneric')
  } else if (summary?.type === 'channel') {
    composerPlaceholder = t('composer.channelPlaceholder', { name: summary.name })
  }

  // Drop-zone bridge. The composer exposes an imperative
  // `addFiles` so a file dropped anywhere on the messages list or
  // composer surface gets routed through the same validation +
  // upload-state path as the `+` button.
  const composerRef = useRef<ChatComposerHandle | null>(null)
  const handleFilesDropped = useCallback((files: File[]) => {
    composerRef.current?.addFiles(files)
  }, [])

  // A 403/404 on the summary means this conversation is gone for the caller
  // (channel moved, archived out of reach, membership removed). Rendering the
  // timeline + composer over it would offer a send box that can only fail.
  const noAccess =
    conversation.error instanceof ApiError &&
    (conversation.error.status === 403 || conversation.error.status === 404)
  if (noAccess) {
    return (
      <div
        ref={paneRef}
        role="alert"
        className="flex h-full min-h-0 flex-1 items-center justify-center p-8"
      >
        <div className="max-w-sm text-center text-sm text-fg-muted">
          <p className="mb-1 text-base font-medium text-fg">{t('noAccess.title')}</p>
          <p className="mb-4">{t('noAccess.body')}</p>
          <Link
            to="/messages"
            className="font-medium text-fg underline underline-offset-2 hover:text-fg-muted focus:outline-none focus:ring-2 focus:ring-accent"
          >
            {t('noAccess.back')}
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div ref={paneRef} className="flex h-full min-h-0 flex-1 flex-col lg:flex-row">
      {/* Main pane — header + timeline (or search results) + composer.
          `min-w-0` lets this flex child shrink below its content's
          min-content width; without it a wide message (e.g. a long
          single-line code block) blows the pane past the viewport and
          defeats `.chat-code-block`'s `max-width:100%` internal scroll. */}
      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
        {summary?.type === 'channel' ? (
          <ChannelHeader
            conversation={summary}
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
          />
        ) : (
          <ConversationHeader
            conversation={summary?.type === 'dm' ? summary : null}
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
          />
        )}
        {/*
         * The dropzone wraps the timeline + typing indicator
         * + composer so a file dropped on any of those surfaces routes
         * through the composer's pending strip. The header stays outside
         * (no point catching drags onto the channel name row).
         */}
        <ChatDropZone onFilesDropped={handleFilesDropped}>
          {searchActive ? (
            <MessageSearchResults
              query={trimmedQuery}
              results={search.data ?? []}
              isLoading={search.isLoading}
              currentUserId={currentUserId}
              members={channelMembers}
            />
          ) : (
            /*
             * `key` forces a remount on conversationId change so MessageList's
             * unread-snapshot ref resets between conversations.
             */
            <MessageList
              key={conversationId}
              conversationId={conversationId}
              focusMessageId={focusMessage ?? null}
              onFocusConsumed={handleFocusConsumed}
            />
          )}
          {/*
           * Phase 4g — typing indicator slots between the timeline and
           * the composer. Fixed `h-7` so the composer doesn't bounce when
           * the indicator appears / disappears; reads typist user ids
           * from the shared typing-state store and resolves names against
           * the conversation summary for "Alice is typing…" rendering.
           */}
          <TypingIndicator conversationId={conversationId} conversation={summary} />
          <ChatComposer
            ref={composerRef}
            conversationId={conversationId}
            placeholder={composerPlaceholder}
          />
        </ChatDropZone>
      </div>
      {/*
       * Phase 4e — thread overlay. The child route (`t/$parentId.tsx`)
       * renders `<ThreadView>`; the index route is null so there's no
       * surface here when the thread is closed.
       */}
      <Outlet />
    </div>
  )
}
